import { NAVIGATION_TAB_CHANGED } from './events';

// Default class names for nav elements
const CLASS_NAME = {
  LINK: 'navtab__link',
  LINK_ACTIVE: 'navtab__link--active',
  TAB_ITEM: 'navtab__item',
  PANE: 'navtab__pane',
  PANE_ACTIVE: 'navtab__pane--active',
  NAVTAB_CONTENT: 'navtab__content'
};

// Default NavTab selector
const DEFAULT_SELECTOR = '.navtab';

export const DEFAULT_OPTIONS = {
  className: CLASS_NAME,
  selector: DEFAULT_SELECTOR
};

class NavTab {
  /**
   * Initializer
   *
   * @param {Element} elementRef - HTML node that functions as a navTab container
   * @param {Object} options - Configuration options, defaults to `DEFAULT_OPTIONS`.
   * */
  constructor(elementRef, options = DEFAULT_OPTIONS) {
    // Initialise attributes
    this.elementRef = elementRef;
    this.options = options;
    this.className = options.className || CLASS_NAME;

    this.tabLinks = elementRef.querySelectorAll(`.${this.className['LINK']}`);
    this.tabPanes = elementRef.querySelectorAll(`.${this.className['PANE']}`);

    // Bind functions
    this.clickHandler = this.clickHandler.bind(this);
    this.reset = this.reset.bind(this);

    this._setup();
  }

  /**
   * Resets the navtab.
   * */
  reset() {
    this._removeEventListeners();
    return new NavTab(this.elementRef, this.options);
  }

  // Event Handlers

  /**
   * Click handler of tabLinks.
   *
   * @param {Event} event - click event on tabLinks
   * */
  clickHandler(event) {
    event.preventDefault();

    let tabLink = event.target.closest(`.${this.className['LINK']}`);
    let linkHref = tabLink.getAttribute('href').substring(1);

    this._updateTabItem(linkHref);
    this._updateTabPane(linkHref);
    this._dispatchNavtabChangedEvent(linkHref);
  }

  // private

  /**
   * Updates all the tab items.
   *  1) Adds `active` class to the tabLink with matching `href`.
   *  1) Removes `active` class from other tabLinks.
   *
   * @param {String} href - tab ID to be activated
   * */
  _updateTabItem(href) {
    this.tabLinks.forEach(tabLink => {
      if (tabLink.getAttribute('href') === `#${href}`) {
        tabLink.classList.add(this.className['LINK_ACTIVE']);
      } else {
        tabLink.classList.remove(this.className['LINK_ACTIVE']);
      }
    });
  }

  /**
   * Updates all the tab panes.
   *  1) Adds `active` class to the tabPane with matching `href`.
   *  1) Removes `active` class from other tabPanes.
   *
   * @param {String} href - tab ID to be activated
   * */
  _updateTabPane(href) {
    this.tabPanes.forEach(tabPane => {
      if (tabPane.getAttribute('id') === href) {
        tabPane.classList.add(this.className['PANE_ACTIVE']);
      } else {
        tabPane.classList.remove(this.className['PANE_ACTIVE']);
      }
    });
  }

  /**
   * Set-up NavTab.
   * */
  _setup() {
    this._addEventListeners();
  }

  /**
   * Adds all the required event listeners.
   * */
  _addEventListeners() {
    this.tabLinks.forEach(tabLink => {
      tabLink.addEventListener('click', this.clickHandler);
    });
  }

  _dispatchNavtabChangedEvent(linkHref) {
    const event = new CustomEvent(NAVIGATION_TAB_CHANGED, { bubbles: true, detail: { tab: linkHref } });
    this.elementRef.dispatchEvent(event);
  }

  /**
   * Removes all the event listeners.
   * */
  _removeEventListeners() {
    this.tabLinks.forEach(tabLink => {
      tabLink.removeEventListener('click', this.clickHandler);
    });
  }
}

export default NavTab;
